import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { OptLogVolumeActionsCountResponse } from '~/shared/types/api';
import { useXFetch } from '~/shared/utils/useXFetch';
import { DEFAULT_REFETCH_INTERVAL_MS } from '~/shared/constants/polling';
import { optLogActionsCountUrl } from '~/shared/components/filters/actionTypeFilter';

export interface UseAllActionsCountParams {
  baseCountQueryString: string;
  enabled?: boolean;
}

export function useAllActionsCount({
  baseCountQueryString,
  enabled = true,
}: UseAllActionsCountParams): {
  allActionsCount: number | undefined;
  allActionsCountLoading: boolean;
} {
  const api = useXFetch();
  const lastCountRef = useRef<number | undefined>(undefined);

  const { data, isLoading } = useQuery({
    queryKey: ['optLog', 'allActionsCount', baseCountQueryString],
    enabled: enabled && !!baseCountQueryString,
    refetchOnWindowFocus: false,
    refetchInterval: DEFAULT_REFETCH_INTERVAL_MS,
    queryFn: () =>
      api<OptLogVolumeActionsCountResponse>(optLogActionsCountUrl(baseCountQueryString)),
  });

  const fetchedCount = data?.count;
  if (fetchedCount !== undefined) {
    lastCountRef.current = fetchedCount;
  }

  const allActionsCount = fetchedCount ?? lastCountRef.current;
  const allActionsCountLoading = allActionsCount === undefined && isLoading;

  return { allActionsCount, allActionsCountLoading };
}
